import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

/**
 * Extension to prevent empty paragraphs being added after headings
 * when clicking on a heading or at certain cursor positions.
 * This filters transactions that would add unwanted empty paragraphs.
 */
export const HeadingCleanup = Extension.create({
  name: 'headingCleanup',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('headingCleanup'),
        filterTransaction(transaction, state) {
          // Allow all transactions that don't change the document
          if (!transaction.docChanged) {
            return true
          }

          // Check if this transaction is adding an empty paragraph after a heading
          // by comparing the new doc structure with the old one
          const oldDoc = state.doc
          const newDoc = transaction.doc

          // If doc size increased and we're not typing text
          if (newDoc.content.size > oldDoc.content.size) {
            // Check if any step is adding text content
            let hasTextContent = false
            transaction.steps.forEach((step) => {
              const json = step.toJSON()
              if (json.slice?.content) {
                json.slice.content.forEach((node: { type?: string; text?: string; content?: unknown[] }) => {
                  if (node.type === 'text' && node.text) {
                    hasTextContent = true
                  }
                  // Also check for paragraph with actual content
                  if (node.type === 'paragraph' && node.content && node.content.length > 0) {
                    hasTextContent = true
                  }
                })
              }
            })

            // If no text content is being added, check for empty paragraph after heading
            if (!hasTextContent) {
              let hasEmptyParaAfterHeading = false

              newDoc.descendants((node, _pos, parent, index) => {
                if (hasEmptyParaAfterHeading) return false

                // Check for empty paragraph
                if (node.type.name === 'paragraph' && node.content.size === 0) {
                  // Check if previous sibling is a heading with content
                  if (parent && index > 0) {
                    const prevSibling = parent.child(index - 1)
                    if (prevSibling.type.name === 'heading' && prevSibling.content.size > 0) {
                      // Verify this empty paragraph didn't exist before
                      let existedBefore = false
                      oldDoc.descendants((oldNode, _oldPos, oldParent, oldIndex) => {
                        if (existedBefore) return false
                        if (oldNode.type.name === 'paragraph' && oldNode.content.size === 0) {
                          if (oldParent && oldIndex > 0) {
                            const oldPrevSibling = oldParent.child(oldIndex - 1)
                            if (oldPrevSibling.type.name === 'heading') {
                              existedBefore = true
                              return false
                            }
                          }
                        }
                      })

                      if (!existedBefore) {
                        hasEmptyParaAfterHeading = true
                        return false
                      }
                    }
                  }
                }
              })

              // Block the transaction if it's adding an empty paragraph after heading
              if (hasEmptyParaAfterHeading) {
                return false
              }
            }
          }

          return true
        }
      })
    ]
  }
})
